import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/Shared";
import { Plus, Trash2, MapPin, RefreshCw, Copy, Check, Users, UserMinus, Pencil, Crown, Sparkles, Layers } from "lucide-react";
import { Modal } from "@/pages/Products";
import { toast } from "sonner";

const PLAN_META = {
  basic:      { label: "Starter", icon: Layers,   cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  pro:        { label: "Growth",  icon: Sparkles, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  enterprise: { label: "Pro",     icon: Crown,    cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function Restaurants() {
  const [list, setList] = useState([]);
  const [org, setOrg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", image_url: "" });
  const [copiedId, setCopiedId] = useState(null);
  const [chefsByRid, setChefsByRid] = useState({});
  const [openChefsFor, setOpenChefsFor] = useState(null);

  const load = async () => {
    const [r, o] = await Promise.all([
      api.get("/restaurants"),
      api.get("/me/organization").catch(() => ({ data: null })),
    ]);
    setList(r.data);
    setOrg(o.data);
  };
  useEffect(() => { load(); }, []);

  const loadChefs = async (rid) => {
    try {
      const { data } = await api.get(`/restaurants/${rid}/chefs`);
      setChefsByRid((m) => ({ ...m, [rid]: data }));
    } catch {}
  };

  const removeChef = async (rid, chefId) => {
    if (!confirm("Chef ontkoppelen van dit restaurant?")) return;
    try {
      await api.delete(`/restaurants/${rid}/chefs/${chefId}`);
      toast.success("Ontkoppeld");
      loadChefs(rid);
    } catch { toast.error("Mislukt"); }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ name: "", location: "", image_url: "" });
    setShowForm(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ name: r.name || "", location: r.location || "", image_url: r.image_url || "" });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.patch(`/restaurants/${editingId}`, form);
        toast.success("Restaurant bijgewerkt");
      } else {
        await api.post("/restaurants", form);
        toast.success("Restaurant toegevoegd — deelcode aangemaakt");
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: "", location: "", image_url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const del = async (id) => { await api.delete(`/restaurants/${id}`); toast.success("Verwijderd"); load(); };

  const regenerate = async (id) => {
    if (!confirm("Nieuwe deelcode genereren? Oude code wordt ongeldig.")) return;
    await api.post(`/restaurants/${id}/regenerate-code`);
    toast.success("Nieuwe deelcode gegenereerd");
    load();
  };

  const copy = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="restaurants-page">
      <PageHeader
        overline="Locaties"
        title="Restaurants"
        description="Beheer locaties en deel codes met chefs zodat ze kunnen aansluiten."
        actions={
          <button onClick={openNew} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="restaurants-add-button">
            <Plus size={16} /> Nieuw restaurant
          </button>
        }
      />

      {org && (() => {
        const meta = PLAN_META[org.plan] || PLAN_META.basic;
        const Icon = meta.icon;
        const limit = org.plan_limit;
        const used = org.usage?.restaurants ?? list.length;
        const atLimit = used >= limit;
        return (
          <div className="card-flat p-4 mb-6 flex items-center gap-4" data-testid="plan-card">
            <div className={`w-10 h-10 rounded-md border ${meta.cls} flex items-center justify-center shrink-0`}>
              <Icon size={18} strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline mb-0.5">Abonnement</div>
              <div className="font-bold text-[#1B2D5C] tracking-tight">{org.name} · {meta.label}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="overline">Restaurants</div>
              <div className={`font-mono text-lg font-bold ${atLimit ? "text-amber-600" : "text-[#1B2D5C]"}`} data-testid="plan-usage">
                {used} / {limit}
              </div>
              {atLimit && (
                <div className="text-[10px] text-amber-600 font-semibold mt-0.5">Limiet bereikt</div>
              )}
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((r) => (
          <div key={r.id} className="card-flat overflow-hidden" data-testid={`restaurant-card-${r.id}`}>
            <div className="aspect-[16/9] bg-zinc-100 bg-cover bg-center" style={{ backgroundImage: r.image_url ? `url(${r.image_url})` : "none" }} />
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-lg tracking-tight text-[#1B2D5C]">{r.name}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-zinc-500 mt-1">
                    <MapPin size={13} /> {r.location}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(r)} className="text-zinc-500 hover:text-[#1B2D5C] p-1" data-testid={`edit-restaurant-${r.id}`} title="Bewerken">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => del(r.id)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`delete-restaurant-${r.id}`} title="Verwijderen">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 mt-3">
                <div className="overline mb-1.5 text-[10px]">Deelcode voor chefs</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono font-bold text-lg tracking-[0.2em] text-[#1B2D5C]" data-testid={`invite-code-${r.id}`}>{r.invite_code || "—"}</code>
                  <button onClick={() => copy(r.invite_code, r.id)} className="p-2 hover:bg-white rounded text-zinc-500 hover:text-[#5BC044] transition-colors" title="Kopiëren" data-testid={`copy-code-${r.id}`}>
                    {copiedId === r.id ? <Check size={15} className="text-[#5BC044]" /> : <Copy size={15} />}
                  </button>
                  <button onClick={() => regenerate(r.id)} className="p-2 hover:bg-white rounded text-zinc-500 hover:text-[#1B2D5C] transition-colors" title="Nieuwe code" data-testid={`regen-code-${r.id}`}>
                    <RefreshCw size={15} />
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  if (openChefsFor === r.id) { setOpenChefsFor(null); return; }
                  setOpenChefsFor(r.id);
                  loadChefs(r.id);
                }}
                className="mt-3 w-full text-left flex items-center justify-between text-sm text-zinc-600 hover:text-[#1B2D5C] py-2 border-t border-zinc-100"
                data-testid={`toggle-chefs-${r.id}`}
              >
                <span className="inline-flex items-center gap-2">
                  <Users size={14} /> Gekoppelde chefs
                </span>
                <span className="text-xs text-zinc-400">{openChefsFor === r.id ? "Verbergen" : "Tonen"}</span>
              </button>
              {openChefsFor === r.id && (
                <div className="mt-2 space-y-1.5" data-testid={`chefs-list-${r.id}`}>
                  {(chefsByRid[r.id] || []).length === 0 ? (
                    <div className="text-xs text-zinc-500 py-2 px-3 bg-zinc-50 rounded-md">Nog geen chefs gekoppeld.</div>
                  ) : (chefsByRid[r.id] || []).map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 border border-zinc-200 rounded-md" data-testid={`chef-${r.id}-${c.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-[#1B2D5C] text-white text-xs font-semibold flex items-center justify-center overflow-hidden">
                          {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover" /> : (c.name?.[0]?.toUpperCase() || "?")}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{c.name}</div>
                          <div className="text-xs text-zinc-500 truncate">{c.email}</div>
                        </div>
                      </div>
                      <button onClick={() => removeChef(r.id, c.id)} className="text-zinc-400 hover:text-red-600 p-1" title="Ontkoppelen" data-testid={`remove-chef-${c.id}`}>
                        <UserMinus size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="col-span-full py-12 text-center text-zinc-500 border border-dashed border-zinc-200 rounded-md">
            Nog geen restaurants.
          </div>
        )}
      </div>

      {showForm && (
        <Modal onClose={() => { setShowForm(false); setEditingId(null); }} title={editingId ? "Restaurant bewerken" : "Nieuw restaurant"}>
          <form onSubmit={submit} className="space-y-3" data-testid="restaurant-form">
            <div>
              <label className="overline block mb-1.5">Naam</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" data-testid="r-name" />
            </div>
            <div>
              <label className="overline block mb-1.5">Locatie</label>
              <input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="form-input" data-testid="r-location" />
            </div>
            <div>
              <label className="overline block mb-1.5">Foto URL</label>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="form-input" placeholder="https://images.unsplash.com/photo-…" data-testid="r-image" />
              {form.image_url && (
                <div className="mt-2 aspect-[16/9] bg-zinc-100 bg-cover bg-center rounded-md border border-zinc-200" style={{ backgroundImage: `url(${form.image_url})` }} />
              )}
            </div>
            <button type="submit" className="w-full h-10 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-md text-sm font-semibold" data-testid="r-submit">
              {editingId ? "Opslaan" : "Toevoegen"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
